import React, {
  useEffect,
  useState
} from 'react';

import axios from 'axios';

import {
  Archive,
  Calendar,
  CheckCircle,
  Download,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Plus,
  RefreshCw,
  Upload
} from 'lucide-react';

import {
  useSettings
} from '../contexts/SettingsContext';

const API_URL =
  process.env.REACT_APP_API_URL ||
  '/api';

const api =
  axios.create({
    baseURL: API_URL,
    withCredentials: true
  });

function baixarBlob(
  resposta,
  nomePadrao
) {
  const url =
    window.URL.createObjectURL(
      new Blob([resposta.data])
    );

  const link =
    document.createElement('a');

  const disposicao =
    resposta.headers[
      'content-disposition'
    ] || '';

  const encontrado =
    disposicao.match(
      /filename="?([^"]+)"?/i
    );

  link.href = url;
  link.download =
      nomePadrao ||
      encontrado?.[1] ||
      'arquivo';

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(url);
}


function somenteNumeros(valor) {
  return String(valor || '')
    .replace(/\D/g, '');
}

function formatarCpf(valor) {
  const numero =
    somenteNumeros(valor);

  if (numero.length !== 11) {
    return String(valor || '');
  }

  return numero.replace(
    /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
    '$1.$2.$3-$4'
  );
}

function formatarCnpj(valor) {
  const numero =
    somenteNumeros(valor);

  if (numero.length !== 14) {
    return String(valor || '');
  }

  return numero.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
}

function hojeInput() {
  const data = new Date();

  const ano =
    data.getFullYear();

  const mes =
    String(
      data.getMonth() + 1
    ).padStart(2, '0');

  const dia =
    String(
      data.getDate()
    ).padStart(2, '0');

  return `${ano}-${mes}-${dia}`;
}

function dataInput(valor) {
  if (!valor) {
    return '';
  }

  const encontrado =
    String(valor).match(
      /^(\d{4}-\d{2}-\d{2})/
    );

  return encontrado
    ? encontrado[1]
    : '';
}

function processoFinalizado(
  processo
) {
  return (
    processo
      ?.nada_consta_confirmado ===
      true ||
    [
      'CONCLUIDO',
      'RESTRICOES_RETORNARAM',
      'REPROTOCOLADO'
    ].includes(
      String(
        processo?.status ||
        ''
      )
    )
  );
}

function dataAtualArquivoLote() {
  const partes = new Intl.DateTimeFormat(
    'pt-BR',
    {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }
  ).formatToParts(new Date());
  const valor = tipo =>
    partes.find(parte => parte.type === tipo)?.value || '';
  return [valor('day'), valor('month'), valor('year')].join(' ');
}

function nomeArquivoLote(
  referencia,
  formato
) {
  let nome =
    String(
      referencia ||
      'AÇÃO'
    ).trim();

  const lote = nome.match(
    /^(?:AÇÃO|ACAO)\s*-\s*\d{8}\s*-\s*(\d+)$/i
  );
  if (lote) {
    nome =
      'Ação - ' + dataAtualArquivoLote() + '-' + lote[1];
  } else {
    nome = nome
      .replace(/^AÇÃO-/i, 'Ação -')
      .replace(/^ACAO-/i, 'Ação -');
  }

  return (
    nome +
    '.' +
    formato
  );
}

const Processes = () => {
  const { darkMode } =
    useSettings();

  const [summary, setSummary] =
    useState({});

  const [batches, setBatches] =
    useState([]);

  const [providers, setProviders] =
    useState([]);

  const [selected, setSelected] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [busy, setBusy] =
    useState(false);

  const [providerId, setProviderId] =
    useState('');

  const [actionDate, setActionDate] =
    useState('');

  const [newProvider, setNewProvider] =
    useState({
      owner_name: '',
      entity_name: ''
    });

  const [documents, setDocuments] =
    useState([]);

  const [documentLead, setDocumentLead] =
    useState(null);

  const [documentType, setDocumentType] =
    useState('PROTESTOS');

  const [uploadFile, setUploadFile] =
    useState(null);

  const [processTab, setProcessTab] =
    useState('ativos');

  const pageText =
    darkMode
      ? 'text-white'
      : 'text-gray-900';

  const secondary =
    darkMode
      ? 'text-gray-400'
      : 'text-gray-600';

  const card =
    darkMode
      ? 'bg-gray-800 border-gray-700'
      : 'bg-white border-gray-200';

  async function carregarTudo() {
    setLoading(true);

    try {
      const [
        resumo,
        lotes,
        executores
      ] = await Promise.all([
        api.get('/processes/summary'),
        api.get('/processes/batches'),
        api.get('/processes/providers')
      ]);

      setSummary(
        resumo.data.data || {}
      );

      setBatches(
        lotes.data.data || []
      );

      setProviders(
        executores.data.data || []
      );
    } catch (error) {
      alert(
        error.response?.data?.error ||
        'Não foi possível carregar os processos.'
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregarTudo();
  }, []);

  async function abrirLote(id) {
    try {
      const resposta =
        await api.get(
          '/processes/batches/' + id
        );

      const dados =
        resposta.data.data;

      setSelected(dados);

      const possuiAtivos =
        (dados.processos || [])
          .some(
            processo =>
              !processoFinalizado(
                processo
              )
          );

      setProcessTab(
        possuiAtivos
          ? 'ativos'
          : 'finalizados'
      );

      setProviderId(
        dados.lote.provider_id ||
        ''
      );

      setActionDate(
        dados.lote.action_date
          ? String(
              dados.lote.action_date
            ).slice(0, 10)
          : ''
      );

      setDocuments([]);
      setDocumentLead(null);
    } catch (error) {
      alert(
        error.response?.data?.error ||
        'Não foi possível abrir o lote.'
      );
    }
  }

  async function salvarExecutor() {
    if (!selected?.lote?.id) {
      return;
    }

    if (!providerId) {
      alert(
        'Selecione a associação ou escritório.'
      );

      return;
    }

    await api.patch(
      '/processes/batches/' +
        selected.lote.id +
        '/provider',
      {
        provider_id:
          Number(providerId),
        action_date:
          actionDate || null
      }
    );
  }

  async function criarExecutor() {
    if (!newProvider.owner_name.trim()) {
      alert(
        'Informe o responsável.'
      );

      return;
    }

    try {
      await api.post(
        '/processes/providers',
        newProvider
      );

      setNewProvider({
        owner_name: '',
        entity_name: ''
      });

      await carregarTudo();

      alert(
        'Associação ou escritório cadastrado.'
      );
    } catch (error) {
      alert(
        error.response?.data?.error ||
        'Não foi possível cadastrar.'
      );
    }
  }

  async function baixarExportacao(
    loteId,
    formato
  ) {
    const resposta =
      await api.get(
        '/processes/batches/' +
          loteId +
          '/export.' +
          formato,
        {
          responseType: 'blob'
        }
      );

    baixarBlob(
        resposta,
        nomeArquivoLote(
          (
            selected?.lote?.id ===
              loteId
              ? selected.lote
              : batches.find(
                  item =>
                    String(item.id) ===
                    String(loteId)
                )
          )?.reference_code,
          formato
        )
      );
  }

  async function fecharEBaixarExcel() {
    if (!selected?.lote?.id) {
      return;
    }

    if (
      !window.confirm(
        'Confirma o fechamento definitivo deste lote? Depois disso, os clientes não entrarão nos próximos arquivos.'
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      await salvarExecutor();

      await api.post(
        '/processes/batches/' +
          selected.lote.id +
          '/close',
        {
          confirmation:
            'FECHADO E EXPORTADO PELO CRM'
        }
      );

      await baixarExportacao(
        selected.lote.id,
        'xlsx'
      );

      await carregarTudo();
      await abrirLote(
        selected.lote.id
      );
    } catch (error) {
      alert(
        error.response?.data?.error ||
        'Não foi possível fechar o lote.'
      );
    } finally {
      setBusy(false);
    }
  }


  async function arquivarLote() {
    if (!selected?.lote?.id) {
      return;
    }

    const referencia =
      selected.lote
        .reference_code;

    if (
      !window.confirm(
        'Deseja excluir este lote da tela?\n\n' +
        'Clientes, documentos e histórico serão preservados.'
      )
    ) {
      return;
    }

    const frase =
      'EXCLUIR ' +
      referencia;

    const digitado =
      window.prompt(
        'Confirmação de segurança.\n\n' +
        'Digite exatamente:\n\n' +
        frase
      );

    if (digitado === null) {
      return;
    }

    if (digitado !== frase) {
      alert(
        'Confirmação incorreta. Nada foi alterado.'
      );

      return;
    }

    setBusy(true);

    try {
      await api.patch(
        '/processes/batches/' +
          selected.lote.id +
          '/archive',
        {
          confirmation:
            digitado
        }
      );

      setSelected(null);

      await carregarTudo();

      alert(
        'Lote retirado da tela. Histórico preservado.'
      );
    } catch (error) {
      alert(
        error.response?.data?.error ||
        'Não foi possível excluir o lote.'
      );
    } finally {
      setBusy(false);
    }
  }

  async function editarDatasCiclo(
    processo
  ) {
    const metadata =
      lerMetadata(processo);

    const retorno =
      window.prompt(
        'Data em que as restrições voltaram (AAAA-MM-DD):',
        dataInput(
          metadata
            .restrictions_returned_at
        )
      );

    if (retorno === null) {
      return;
    }

    const payload = {};

    if (retorno.trim()) {
      payload
        .restrictions_returned_at =
        retorno.trim();
    }

    if (
      metadata
        .reprotocolled_at
    ) {
      const reprotocolo =
        window.prompt(
          'Data da reprotocolização (AAAA-MM-DD):',
          dataInput(
            metadata
              .reprotocolled_at
          )
        );

      if (reprotocolo === null) {
        return;
      }

      if (reprotocolo.trim()) {
        payload
          .reprotocolled_at =
          reprotocolo.trim();
      }
    }

    if (
      !Object.keys(payload)
        .length
    ) {
      return;
    }

    setBusy(true);

    try {
      await api.patch(
        '/processes/actions/' +
          processo.id +
          '/dates',
        payload
      );

      await carregarTudo();

      if (selected?.lote?.id) {
        await abrirLote(
          selected.lote.id
        );
      }

      alert(
        'Datas atualizadas sem reiniciar a garantia.'
      );
    } catch (error) {
      alert(
        error.response?.data?.error ||
        'Não foi possível atualizar as datas.'
      );
    } finally {
      setBusy(false);
    }
  }

  async function abrirDocumentos(
    processo
  ) {
    try {
      const resposta =
        await api.get(
          '/client-documents/lead/' +
            processo.lead_id
        );

      setDocuments(
        resposta.data.data || []
      );

      setDocumentLead(processo);
    } catch (error) {
      alert(
        'Não foi possível carregar os documentos.'
      );
    }
  }

  async function baixarDocumento(
    documento
  ) {
    const resposta =
      await api.get(
        '/client-documents/' +
          documento.id +
          '/download',
        {
          responseType: 'blob'
        }
      );

    baixarBlob(
      resposta,
      documento.original_file_name
    );
  }

  async function enviarDocumento() {
    if (
      !documentLead ||
      !uploadFile
    ) {
      alert(
        'Selecione um arquivo.'
      );

      return;
    }

    const form =
      new FormData();

    form.append(
      'arquivo',
      uploadFile
    );

    form.append(
      'document_type',
      documentType
    );

    form.append(
      'service_action_id',
      documentLead.id
    );

    try {
      await api.post(
        '/client-documents/lead/' +
          documentLead.lead_id,
        form
      );

      setUploadFile(null);

      await abrirDocumentos(
        documentLead
      );

      alert(
        'Documento salvo com segurança.'
      );
    } catch (error) {
      alert(
        error.response?.data?.error ||
        'Não foi possível enviar o documento.'
      );
    }
  }


  // CARDS_CICLOS_ACAO_V9
  function lerMetadata(processo) {
    if (!processo?.metadata) {
      return {};
    }

    if (typeof processo.metadata === 'object') {
      return processo.metadata;
    }

    try {
      return JSON.parse(processo.metadata);
    } catch (error) {
      return {};
    }
  }

  function formatarData(valor) {
    if (!valor) {
      return '—';
    }

    const data =
      new Date(valor);

    if (Number.isNaN(data.getTime())) {
      return '—';
    }

    return data.toLocaleDateString(
      'pt-BR',
      {
        timeZone:
          'America/Sao_Paulo'
      }
    );
  }

  function calcularDias(inicio, fim) {
    if (!inicio || !fim) {
      return null;
    }

    const dataInicio =
      new Date(inicio);

    const dataFim =
      new Date(fim);

    if (
      Number.isNaN(dataInicio.getTime()) ||
      Number.isNaN(dataFim.getTime())
    ) {
      return null;
    }

    return Math.max(
      0,
      Math.ceil(
        (
          dataFim.getTime() -
          dataInicio.getTime()
        ) /
        86400000
      )
    );
  }

  function textoDias(valor) {
    if (valor === null) {
      return '—';
    }

    return (
      valor +
      (
        valor === 1
          ? ' dia'
          : ' dias'
      )
    );
  }

  async function atualizarCiclo(
    processo,
    operacao
  ) {
    const confirmacoes = {
      complete:
        'Confirma que a ação foi finalizada e o Nada Consta já saiu?',
      'restrictions-returned':
        'Confirma que as restrições voltaram para este cliente?',
      reprotocol:
        'Confirma a criação de um novo ciclo de reprotocolização?'
    };

    if (
      !window.confirm(
        confirmacoes[operacao]
      )
    ) {
      return;
    }

    setBusy(true);

    try {
      const rota =
        '/processes/actions/' +
        processo.id +
        '/' +
        operacao;

      const payload = {};

      if (
        operacao ===
        'restrictions-returned'
      ) {
        const data =
          window.prompt(
            'Data em que as restrições voltaram (AAAA-MM-DD):',
            hojeInput()
          );

        if (data === null) {
          return;
        }

        if (!data.trim()) {
          alert(
            'Informe a data.'
          );

          return;
        }

        payload.date =
          data.trim();
      }

      if (
        operacao ===
        'reprotocol'
      ) {
        const data =
          window.prompt(
            'Data da reprotocolização (AAAA-MM-DD):',
            hojeInput()
          );

        if (data === null) {
          return;
        }

        if (!data.trim()) {
          alert(
            'Informe a data.'
          );

          return;
        }

        payload.date =
          data.trim();
      }

      if (
        operacao ===
        'reprotocol'
      ) {
        await api.post(
          rota,
          payload
        );
      } else {
        await api.patch(
          rota,
          payload
        );
      }

      await carregarTudo();

      if (selected?.lote?.id) {
        await abrirLote(
          selected.lote.id
        );
      }
    } catch (error) {
      alert(
        error.response?.data?.error ||
        'Não foi possível atualizar o ciclo.'
      );
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className={pageText}>
        Carregando processos...
      </div>
    );
  }

  const cards = [
    [
      'Lotes abertos',
      summary.lotes_abertos || 0,
      FolderOpen
    ],
    [
      'Lotes fechados',
      summary.lotes_fechados || 0,
      Archive
    ],
    [
      'Aguardando',
      summary.clientes_aguardando || 0,
      Calendar
    ],
    [
      'Concluídos',
      summary.processos_concluidos || 0,
      CheckCircle
    ]
  ];


  const bureauDone = (key) => {
    const md = selected?.lote?.metadata || {};
    const b = md?.bureau_notifications?.[key];
    return b?.status === 'sent';
  };

  const actionCompleted = () => {
    const md = selected?.lote?.metadata || {};
    return md?.action_completed?.status === 'sent';
  };

  const bureauButtonStyle = (done, active, border) => ({
    minHeight: 38,
    padding: '8px 14px',
    borderRadius: 9,
    fontWeight: 800,
    fontSize: 13,
    lineHeight: 1.2,
    color: '#ffffff',
    opacity: 1,
    backgroundColor: done ? '#374151' : active,
    border: `1px solid ${done ? '#9ca3af' : border}`,
    boxShadow: done ? 'none' : '0 2px 8px rgba(0,0,0,.28)'
  });

  const avisarBaixa = async (bureau) => {
    if (!selected?.lote?.id || busy) return;

    const label = bureau === 'BOA_VISTA' ? 'BOA VISTA' : bureau;

    if (!window.confirm(
      `Confirmar que a baixa da ${label} já foi realizada?\n\n` +
      `Ao confirmar, o WhatsApp enviará a comunicação para todos os clientes desta ação.`
    )) return;

    try {
      setBusy(true);

      await api.post(
        `/processes/batches/${selected.lote.id}/bureau-notify`,
        { bureau }
      );

      window.location.reload();

    } catch (err) {

      alert(
        err?.response?.data?.error ||
        err?.message ||
        'Não foi possível enviar a comunicação da baixa.'
      );

      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className={'text-3xl font-bold ' + pageText}>
            Processos
          </h1>

          <p className={secondary}>
            Lotes semanais, executores e documentos da ação
          </p>
        </div>

        <button
          onClick={carregarTudo}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <RefreshCw size={18} />
          Atualizar
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {cards.map(
          ([label, total, Icon]) => (
            <div
              key={label}
              className={'rounded-xl border p-4 ' + card}
            >
              <div className="flex items-center justify-between">
                <span className={secondary}>
                  {label}
                </span>

                <Icon
                  className="text-blue-500"
                  size={22}
                />
              </div>

              <div className={'mt-2 text-3xl font-bold ' + pageText}>
                {total}
              </div>
            </div>
          )
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <section className={'rounded-xl border p-4 xl:col-span-2 ' + card}>
          <h2 className={'mb-4 text-xl font-semibold ' + pageText}>
            Lotes
          </h2>

          <div className="space-y-3">
            {batches.length === 0 && (
              <p className={secondary}>
                Nenhum lote criado. O primeiro será aberto automaticamente quando um cliente pago concluir o envio dos documentos.
              </p>
            )}

            {batches.map(lote => (
              <button
                key={lote.id}
                onClick={() =>
                  abrirLote(lote.id)
                }
                className={'w-full rounded-lg border p-4 text-left transition hover:border-blue-500 ' +
                  (
                    selected?.lote?.id === lote.id
                      ? 'border-blue-500'
                      : (
                          darkMode
                            ? 'border-gray-700'
                            : 'border-gray-200'
                        )
                  )}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className={'font-semibold ' + pageText}>
                      {lote.reference_code}
                    </div>

                    <div className={'text-sm ' + secondary}>
                      {lote.provider_entity_name ||
                       lote.provider_owner_name ||
                       'Executor não escolhido'}
                    </div>
                  </div>

                  <div className="text-right">
                    <span className={'rounded-full px-3 py-1 text-xs font-semibold ' +
                      (
                        lote.status === 'ABERTO'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-green-100 text-green-800'
                      )}
                    >
                      {lote.status}
                    </span>

                    <div className={'mt-2 text-sm ' + secondary}>
                      {lote.total_processos || 0} contratação(ões)
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className={'rounded-xl border p-4 ' + card}>
          <h2 className={'mb-4 text-xl font-semibold ' + pageText}>
            Novo executor
          </h2>

          <div className="space-y-3">
            <input
              value={newProvider.owner_name}
              onChange={event =>
                setNewProvider({
                  ...newProvider,
                  owner_name:
                    event.target.value
                })
              }
              placeholder="Responsável"
              className="w-full rounded-lg border px-3 py-2 text-gray-900"
            />

            <input
              value={newProvider.entity_name}
              onChange={event =>
                setNewProvider({
                  ...newProvider,
                  entity_name:
                    event.target.value
                })
              }
              placeholder="Associação ou escritório"
              className="w-full rounded-lg border px-3 py-2 text-gray-900"
            />

            <button
              onClick={criarExecutor}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
            >
              <Plus size={18} />
              Cadastrar
            </button>
          </div>
        </section>
      </div>

      {selected && (
        <section className={'rounded-xl border p-5 ' + card}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className={'text-xl font-semibold ' + pageText}>
                {selected.lote.reference_code}
              </h2>

              <p className={secondary}>
                {selected.processos.length} contratação(ões) neste lote
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {selected.lote.status === 'ABERTO' ? (
                <button
                  disabled={busy}
                  onClick={fecharEBaixarExcel}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white disabled:opacity-50"
                >
                  <FileSpreadsheet size={18} />
                  {busy
                    ? 'Fechando...'
                    : 'Fechar e baixar Excel'}
                </button>
              ) : (
                <>
                  <button
                    onClick={() =>
                      baixarExportacao(
                        selected.lote.id,
                        'xlsx'
                      )
                    }
                    className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white"
                  >
                    <Download size={18} />
                    Excel
                  </button>

                  <button
                    onClick={() =>
                      baixarExportacao(
                        selected.lote.id,
                        'pdf'
                      )
                    }
                    className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white"
                  >
                    <FileText size={18} />
                    PDF
                  </button>
                </>
              )}
            </div>
                <div className="bg-slate-800 border-2 border-slate-600 rounded-xl p-5 shadow-lg text-slate-100">
                  <div className="mb-3">
                    <div className="font-semibold">Baixas de birôs</div>
                    <div className="text-sm opacity-70">Clique somente após realizar a baixa no respectivo birô.</div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy || bureauDone('serasa')}
                      onClick={() => avisarBaixa('SERASA')}
                      className="rounded-lg"
                      style={bureauButtonStyle(bureauDone('serasa'), '#0f766e', '#5eead4')}
                    >
                      {bureauDone('serasa') ? '✓ SERASA' : 'SERASA'}
                    </button>

                    <button
                      type="button"
                      disabled={busy || bureauDone('boa_vista')}
                      onClick={() => avisarBaixa('BOA_VISTA')}
                      className="rounded-lg"
                      style={bureauButtonStyle(bureauDone('boa_vista'), '#1d4ed8', '#93c5fd')}
                    >
                      {bureauDone('boa_vista') ? '✓ BOA VISTA' : 'BOA VISTA'}
                    </button>

                    <button
                      type="button"
                      disabled={busy || bureauDone('spc')}
                      onClick={() => avisarBaixa('SPC')}
                      className="rounded-lg"
                      style={bureauButtonStyle(bureauDone('spc'), '#7c3aed', '#c4b5fd')}
                    >
                      {bureauDone('spc') ? '✓ SPC' : 'SPC'}
                    </button>
                  </div>

                  {actionCompleted() && (
                    <div className="mt-3 text-sm font-semibold">
                      AÇÃO 100% CONCLUÍDA
                    </div>
                  )}
                </div>

          </div>

          <div className="mt-4 flex justify-end">

            <button

              disabled={busy}

              onClick={arquivarLote}

              className="rounded-lg border border-red-500 px-4 py-2 font-semibold text-red-500 hover:bg-red-500 hover:text-white disabled:opacity-50"

            >

              Excluir lote

            </button>

          </div>


          {selected.lote.status === 'ABERTO' && (
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <label className={secondary}>
                Associação ou escritório

                <select
                  value={providerId}
                  onChange={event =>
                    setProviderId(
                      event.target.value
                    )
                  }
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-gray-900"
                >
                  <option value="">
                    Selecione
                  </option>

                  {providers
                    .filter(item =>
                      item.ativo !== false
                    )
                    .map(item => (
                      <option
                        key={item.id}
                        value={item.id}
                      >
                        {item.entity_name ||
                         item.owner_name}
                      </option>
                    ))}
                </select>
              </label>

              <label className={secondary}>
                Data da ação

                <input
                  type="date"
                  value={actionDate}
                  onChange={event =>
                    setActionDate(
                      event.target.value
                    )
                  }
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-gray-900"
                />
              </label>
            </div>
          )}


            <div className="mt-5 space-y-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() =>
                      setProcessTab(
                        'ativos'
                      )
                    }
                    className={
                      'rounded-lg px-4 py-2 font-semibold ' +
                      (
                        processTab === 'ativos'
                          ? 'bg-blue-600 text-white'
                          : darkMode
                            ? 'bg-gray-700 text-gray-200'
                            : 'bg-gray-200 text-gray-700'
                      )
                    }
                  >
                    Em andamento ({
                      selected.processos
                        .filter(
                          item =>
                            !processoFinalizado(
                              item
                            )
                        )
                        .length
                    })
                  </button>

                  <button
                    onClick={() =>
                      setProcessTab(
                        'finalizados'
                      )
                    }
                    className={
                      'rounded-lg px-4 py-2 font-semibold ' +
                      (
                        processTab ===
                        'finalizados'
                          ? 'bg-green-600 text-white'
                          : darkMode
                            ? 'bg-gray-700 text-gray-200'
                            : 'bg-gray-200 text-gray-700'
                      )
                    }
                  >
                    Finalizados ({
                      selected.processos
                        .filter(
                          processoFinalizado
                        )
                        .length
                    })
                  </button>
                </div>

                {selected.processos
                  .filter(
                    processo =>
                      processTab ===
                      'finalizados'
                        ? processoFinalizado(
                            processo
                          )
                        : !processoFinalizado(
                            processo
                          )
                  )
                  .map(processo => {
                const metadata =
                    lerMetadata(processo);

                  const snapshot =
                    metadata
                      .export_snapshot ||
                    {};

                  const documento =
                    processo.cpf_cnpj ||
                    processo.document_snapshot ||
                    '';

                  const cpfCard =
                    snapshot.cpf ||
                    (
                      somenteNumeros(
                        documento
                      ).length === 11
                        ? documento
                        : ''
                    );

                  const cnpjCard =
                    snapshot.cnpj ||
                    (
                      somenteNumeros(
                        documento
                      ).length === 14
                        ? documento
                        : ''
                    );

                  const finalizada =
                    processoFinalizado(
                      processo
                    );

                const restricoesVoltaram =
                  ['RESTRICOES_RETORNARAM', 'REPROTOCOLADO']
                    .includes(processo.status) ||
                  Boolean(metadata.restrictions_returned_at);

                const reprotocolada =
                  processo.status === 'REPROTOCOLADO' ||
                  Boolean(metadata.reprotocolled_at);

                const inicioCiclo =
                  processo.included_at ||
                  processo.action_date ||
                  processo.created_at;

                const fimCiclo =
                  processo.nada_consta_at;

                const retornoEm =
                  metadata.restrictions_returned_at;

                const reprotocoladaEm =
                  metadata.reprotocolled_at;

                return (
                  <article
                    key={processo.id}
                    className={
                      'rounded-xl border p-4 ' +
                      (
                        darkMode
                          ? 'border-gray-700 bg-gray-900/40'
                          : 'border-gray-200 bg-gray-50'
                      )
                    }
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className={'text-lg font-semibold ' + pageText}>
                          {processo.lead_nome ||
                           processo.client_name_snapshot}
                        </div>

                        {cpfCard && (
                            <div className={'mt-1 text-sm ' + secondary}>
                              CPF: {formatarCpf(cpfCard)}
                            </div>
                          )}

                          {cnpjCard && (
                            <div className={'mt-1 text-sm ' + secondary}>
                              CNPJ: {formatarCnpj(cnpjCard)}
                            </div>
                          )}

                          <div className={'mt-1 text-sm ' + secondary}>
                            Garantia contratada:{' '}
                            {
                              Number(
                                processo
                                  .garantia_meses ||
                                0
                              ) > 0
                                ? (
                                    processo
                                      .garantia_meses +
                                    (
                                      Number(
                                        processo
                                          .garantia_meses
                                      ) === 1
                                        ? ' mês'
                                        : ' meses'
                                    )
                                  )
                                : 'Sem garantia'
                            }
                          </div>

                        <div className={'mt-1 text-sm ' + secondary}>
                          Ciclo {processo.cycle_number || 1}
                          {
                            processo.cycle_type === 'RETURN' ||
                            processo.entry_type === 'RETORNO'
                              ? ' — Retorno'
                              : ' — Inicial'
                          }
                        </div>
                      </div>

                      <span
                        className={
                          'rounded-full px-3 py-1 text-xs font-semibold ' +
                          (
                            reprotocolada
                              ? 'bg-blue-100 text-blue-800'
                              : restricoesVoltaram
                                ? 'bg-red-100 text-red-800'
                                : finalizada
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-amber-100 text-amber-800'
                          )
                        }
                      >
                        {
                          reprotocolada
                            ? 'REPROTOCOLADA'
                            : restricoesVoltaram
                              ? 'RESTRIÇÕES VOLTARAM'
                              : finalizada
                                ? 'FINALIZADA'
                                : 'EM ANDAMENTO'
                        }
                      </span>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <div className={'text-xs ' + secondary}>
                          Início do ciclo
                        </div>
                        <div className={'font-semibold ' + pageText}>
                          {formatarData(inicioCiclo)}
                        </div>
                      </div>

                      <div>
                        <div className={'text-xs ' + secondary}>
                          Finalização
                        </div>
                        <div className={'font-semibold ' + pageText}>
                          {formatarData(fimCiclo)}
                        </div>
                        <div className={'text-xs ' + secondary}>
                          Tempo para finalizar:{' '}
                          {textoDias(
                            calcularDias(
                              inicioCiclo,
                              fimCiclo
                            )
                          )}
                        </div>
                      </div>

                      <div>
                        <div className={'text-xs ' + secondary}>
                          Restrições voltaram
                        </div>
                        <div className={'font-semibold ' + pageText}>
                          {formatarData(retornoEm)}
                        </div>
                        <div className={'text-xs ' + secondary}>
                          Tempo que a ação durou:{' '}
                          {textoDias(
                            calcularDias(
                              fimCiclo,
                              retornoEm
                            )
                          )}
                        </div>
                      </div>

                      <div>
                        <div className={'text-xs ' + secondary}>
                          Reprotocolização
                        </div>
                        <div className={'font-semibold ' + pageText}>
                          {formatarData(reprotocoladaEm)}
                        </div>
                        <div className={'text-xs ' + secondary}>
                          Tempo para reprotocolar:{' '}
                          {textoDias(
                            calcularDias(
                              retornoEm,
                              reprotocoladaEm
                            )
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <div>
                        <div className={'text-xs ' + secondary}>
                          Garantia iniciada
                        </div>
                        <div className={'font-medium ' + pageText}>
                          {
                              Number(
                                processo
                                  .garantia_meses ||
                                0
                              ) > 0
                                ? formatarData(
                                    processo
                                      .guarantee_started_at
                                  )
                                : 'Sem garantia contratada'
                            }
                        </div>
                      </div>

                      <div>
                        <div className={'text-xs ' + secondary}>
                          Garantia até
                        </div>
                        <div className={'font-medium ' + pageText}>
                          {
                              Number(
                                processo
                                  .garantia_meses ||
                                0
                              ) > 0
                                ? formatarData(
                                    processo
                                      .guarantee_ends_at
                                  )
                                : 'Sem garantia contratada'
                            }
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() =>
                          abrirDocumentos(processo)
                        }
                        className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        <FolderOpen size={16} />
                        Documentos ({processo.total_documentos || 0})
                      </button>

                      {!finalizada && (
                        <button
                          disabled={busy}
                          onClick={() =>
                            atualizarCiclo(
                              processo,
                              'complete'
                            )
                          }
                          className="rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          Finalizar ação
                        </button>
                      )}

                      {finalizada &&
                       !restricoesVoltaram && (
                        <button
                          disabled={busy}
                          onClick={() =>
                            atualizarCiclo(
                              processo,
                              'restrictions-returned'
                            )
                          }
                          className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Restrições voltaram
                        </button>
                      )}

                      {restricoesVoltaram &&
                       !reprotocolada && (
                        <button
                          disabled={busy}
                          onClick={() =>
                            atualizarCiclo(
                              processo,
                              'reprotocol'
                            )
                          }
                          className="rounded-lg bg-purple-600 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
                        >
                          Reprotocolada
                        </button>
                      )}

                        {(restricoesVoltaram ||
                          reprotocolada) && (
                          <button
                            disabled={busy}
                            onClick={() =>
                              editarDatasCiclo(
                                processo
                              )
                            }
                            className="rounded-lg border border-blue-500 px-3 py-2 text-sm font-semibold text-blue-500 hover:bg-blue-500 hover:text-white disabled:opacity-50"
                          >
                            Editar datas
                          </button>
                        )}
                    </div>
                  </article>
                );
              })}

              {selected.processos
                  .filter(
                    processo =>
                      processTab ===
                      'finalizados'
                        ? processoFinalizado(
                            processo
                          )
                        : !processoFinalizado(
                            processo
                          )
                  )
                  .length === 0 && (
                  <p className={secondary}>
                    Nenhuma ação nesta aba.
                  </p>
                )}
            </div>
        </section>
      )}

      {documentLead && (
        <section className={'rounded-xl border p-5 ' + card}>
          <h2 className={'text-xl font-semibold ' + pageText}>
            Documentos — {documentLead.lead_nome}
          </h2>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <select
              value={documentType}
              onChange={event =>
                setDocumentType(
                  event.target.value
                )
              }
              className="rounded-lg border px-3 py-2 text-gray-900"
            >
              <option value="RG_CNH">RG ou CNH</option>
              <option value="COMPROVANTE_ENDERECO">Comprovante de endereço</option>
              <option value="PROTESTOS">Consulta de Protestos</option>
              <option value="CONSULTA_SERASA">Consulta Serasa</option>
              <option value="CONSULTA_BOA_VISTA">Consulta Boa Vista</option>
              <option value="CONTRATO">Contrato</option>
              <option value="NADA_CONSTA">Nada consta</option>
              <option value="OUTRO">Outro</option>
            </select>

            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={event =>
                setUploadFile(
                  event.target.files?.[0] ||
                  null
                )
              }
              className={'rounded-lg border p-2 ' + pageText}
            />

            <button
              onClick={enviarDocumento}
              className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white"
            >
              <Upload size={18} />
              Enviar documento
            </button>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {documents.map(documento => (
              <button
                key={documento.id}
                onClick={() =>
                  baixarDocumento(
                    documento
                  )
                }
                className={'rounded-lg border p-3 text-left hover:border-blue-500 ' +
                  (
                    darkMode
                      ? 'border-gray-700'
                      : 'border-gray-200'
                  )}
              >
                <div className="flex items-center gap-2">
                  <FileText
                    size={18}
                    className="text-blue-500"
                  />

                  <span className={'font-medium ' + pageText}>
                    {documento.display_name}
                  </span>
                </div>

                <div className={'mt-1 truncate text-xs ' + secondary}>
                  {documento.original_file_name}
                </div>
              </button>
            ))}

            {documents.length === 0 && (
              <p className={secondary}>
                Nenhum documento disponível.
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Processes;
